import { useMemo } from "react";
import { QuestionDTO } from "../../../services/apiClient";
import Button from "../../common/Button";
import ImagePreview from "../../common/ImagePreview";
import Card from "../../common/Card";

interface Props {
    title: string;
    currentStep?: number;
    data: QuestionDTO[];
    selectedAnswers: Record<string, string>;
    onAnswerChange: (questionId: string, userAnswerId: string) => void;
    onNext: () => void;
    onPrevious: () => void;
    showCorrectAnswers?: boolean;
}

const QuestionSection: React.FC<Props> = ({ title, currentStep, data, selectedAnswers, onAnswerChange, onNext, onPrevious, showCorrectAnswers }) => {

    // Randomize questions and their options
    const randomizedData = useMemo(() => {
        const shuffleArray = <T,>(array: T[]): T[] => {
            const shuffled = [...array];
            for (let i = shuffled.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
            return shuffled;
        };

        // Randomize the questions array
        const shuffledQuestions = shuffleArray(data);

        // Randomize options for each question
        return shuffledQuestions.map(question => ({
            ...question,
            questionOptions: question.questionOptions ? (question.rotateOptions !== false ? shuffleArray(question.questionOptions) : question.questionOptions)
                : undefined
        }));
    }, [data]);

    const handleOptionChange = (questionId: string, optionId: string) => {
        onAnswerChange(questionId, optionId);
    };

    const scrollToTop = () => {
        // Multiple scroll methods with delay for DOM update
        setTimeout(() => {
            // Method 1: Standard window scroll
            window.scrollTo({ top: 0, behavior: 'smooth' });

            // Method 2: Immediate scroll fallback
            setTimeout(() => {
                window.scrollTo(0, 0);
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;

                // Method 3: Try scrolling the main container
                const mainElement = document.querySelector('main');
                if (mainElement) {
                    mainElement.scrollTop = 0;
                }

                // Method 4: Try scrolling any scrollable parent
                const scrollableElement = document.querySelector('.overflow-auto, .overflow-y-auto, .overflow-scroll');
                if (scrollableElement) {
                    scrollableElement.scrollTop = 0;
                }
            }, 50);
        }, 0);
    };

    const handleNext = async () => {
        scrollToTop();
        onNext();
    };

    const handlePrevious = async () => {
        scrollToTop();
        onPrevious();
    };

    return (

        <Card title={title} subtitle={`Total Questions: ${data.length}`}>
            {/* Questions Section */}
            <div className="space-y-6">

                {randomizedData.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        No questions found for this section.
                    </div>
                ) : (
                    randomizedData.map((question, index) => {
                        const questionId = question.id || index.toString();
                        const hasSelection = selectedAnswers[questionId] !== undefined;

                        return (
                            <div
                                key={question.id || index}
                                className={`p-4 rounded-lg border-2 ${hasSelection
                                    ? 'border-orange-100'
                                    : 'border-gray-200 bg-white'
                                    }`}
                            >
                                <h3 className="text-lg font-semibold mb-4">
                                    {index + 1}.{question.title}
                                </h3>

                                {/* Question Image */}
                                {question.imageId && (
                                    <div className="mb-4">
                                        <ImagePreview fileId={question.imageId} size="xlg" />
                                    </div>
                                )}

                                {/* Question Options */}
                                {question.questionOptions && question.questionOptions.length > 0 && (
                                    <div className="space-y-3">
                                        {question.questionOptions.map((option, optionIndex) => {
                                            const questionId = question.id || index.toString();
                                            const isSelected = selectedAnswers[questionId] === option.id;
                                            const isCorrect = showCorrectAnswers && option.isCorrectOption;

                                            return (
                                                <label
                                                    key={option.id || optionIndex}
                                                    className={`flex items-start space-x-3 cursor-pointer p-3 rounded border ${isCorrect ? 'border-l-4 border-l-green-500' : ''} ${hasSelection
                                                        ? 'border-orange-200 hover:bg-orange-100'
                                                        : 'border-gray-300 hover:bg-gray-50'
                                                        } ${isSelected ? 'bg-orange-50' : ''} ${isCorrect ? 'bg-green-50' : ''}`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name={`question-${questionId}`}
                                                        value={option.id}
                                                        checked={isSelected}
                                                        className={`mt-1 ${hasSelection
                                                            ? 'text-orange-400 accent-orange-600'
                                                            : 'text-gray-400 accent-gray-400'
                                                            }`}
                                                        onChange={() => handleOptionChange(questionId, option.id || '')}
                                                    />
                                                    <span className={`flex-1 ${hasSelection
                                                        ? (isSelected ? 'text-orange-400 font-bold' : 'text-orange-500')
                                                        : ''
                                                        }`}>
                                                        {option.title}
                                                    </span>
                                                    {isCorrect && (
                                                        <span className="text-green-600 font-bold ml-2" title="Correct Answer">✓</span>
                                                    )}
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}

            </div>

            {/* Navigation Buttons */}
            <div className={`mt-5 flex ${currentStep === 3 ? 'justify-end' : 'justify-between'}`}>

                {currentStep !== 3 && (
                    <Button onClick={handlePrevious} variant="primary">
                        Previous
                    </Button>
                )}

                <Button onClick={handleNext} variant="primary">
                    Next
                </Button>
            </div>
        </Card>



    );

};

export default QuestionSection;